import React, { useCallback } from 'react';
import css from './PulseCell.module.css';

/**
 * PulseCell — shared stat/pulse cell primitive
 *
 * Props:
 *   number      {string|number}  Primary displayed value
 *   label       {string}         Label beneath the number
 *   sub         {string?}        Optional small sub-label (Dashboard only)
 *   onClick     {function?}      If provided, cell is interactive
 *   ariaLabel   {string?}        Accessibility label (defaults to "label: number")
 *   variant     {'default'|'large'|'warn'|'positive'}  Visual variant
 *   numberColor {string?}        Inline CSS color for the number (overrides variant color)
 *   className   {string?}        Extra className on the root element
 */
export default function PulseCell({
  number,
  label,
  sub,
  onClick,
  ariaLabel,
  variant = 'default',
  numberColor,
  className,
  isActive = false,
}) {
  const isInteractive = typeof onClick === 'function';

  const onKeyDown = useCallback(e => {
    if (!isInteractive) return;
    if (e.key === 'Enter') { onClick(); }
    if (e.key === ' ')     { e.preventDefault(); onClick(); }
  }, [isInteractive, onClick]);

  const rootClass = [
    css.cell,
    variant === 'large'    ? css.large    : '',
    variant === 'warn'     ? css.warn     : '',
    variant === 'positive' ? css.positive : '',
    isInteractive          ? css.interactive : '',
    isActive               ? css.active   : '',
    className || '',
  ].filter(Boolean).join(' ');

  const numStyle = numberColor ? { color: numberColor } : undefined;

  return (
    <div
      className={rootClass}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={isInteractive ? onKeyDown : undefined}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={isInteractive ? (ariaLabel || `${label}: ${number}`) : undefined}
    >
      <div className={css.number} style={numStyle}>{number}</div>
      <div className={css.label}>{label}</div>
      {sub && <div className={css.sub}>{sub}</div>}
    </div>
  );
}
